import { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { X, Clock, Calendar, Trash2 } from 'lucide-react';
import type { AvailabilityRule, DateSpecificAvailability, PTODay } from '../hooks/useAvailability';

interface DayEditorPopoverProps {
  date: Date;
  standardRule: AvailabilityRule | null;
  currentOverride: DateSpecificAvailability | null;
  currentPto: PTODay | null;
  position: { x: number; y: number };
  onSaveCustomHours: (data: { startTime: string; endTime: string }) => void;
  onClearOverride: () => void;
  onSetPto: () => void;
  onClearPto: () => void;
  onClose: () => void;
  isSaving?: boolean;
}

export function DayEditorPopover({
  date,
  standardRule,
  currentOverride,
  currentPto,
  position,
  onSaveCustomHours,
  onClearOverride,
  onSetPto,
  onClearPto,
  onClose,
  isSaving = false,
}: DayEditorPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);

  // Initialize form with existing override or standard hours
  const defaultStart = currentOverride?.startTime ?? standardRule?.startTime ?? '08:00';
  const defaultEnd = currentOverride?.endTime ?? standardRule?.endTime ?? '17:00';

  const [startTime, setStartTime] = useState(defaultStart.substring(0, 5));
  const [endTime, setEndTime] = useState(defaultEnd.substring(0, 5));

  // Handle click outside to close
  // Use a small delay to avoid closing immediately when the popover opens
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    // Delay adding the listener to avoid catching the click that opened the popover
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Calculate position to keep popover in viewport
  const getPositionStyle = () => {
    const style: React.CSSProperties = {
      position: 'fixed',
      zIndex: 50,
    };

    // Try to position below and to the right of the click
    const popoverWidth = 280;
    const popoverHeight = 320;
    const padding = 16;

    let left = position.x;
    let top = position.y + 8;

    // Adjust if would go off right edge
    if (left + popoverWidth > window.innerWidth - padding) {
      left = window.innerWidth - popoverWidth - padding;
    }

    // Adjust if would go off bottom edge
    if (top + popoverHeight > window.innerHeight - padding) {
      top = position.y - popoverHeight - 8;
    }

    // Ensure not off left edge
    if (left < padding) {
      left = padding;
    }

    // Ensure not off top edge
    if (top < padding) {
      top = padding;
    }

    style.left = left;
    style.top = top;

    return style;
  };

  const handleSaveCustomHours = () => {
    onSaveCustomHours({ startTime, endTime });
  };

  const hasPto = currentPto !== null;
  const hasOverride = currentOverride !== null;
  const hasStandardHours = standardRule !== null;

  return (
    <>
      {/* Popover */}
      <div
        ref={popoverRef}
        style={getPositionStyle()}
        className="bg-background rounded-lg border border-border shadow-xl w-[280px] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-border bg-background-subtle">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-foreground-muted" />
            <span className="font-semibold text-foreground">
              {format(date, 'EEEE, MMM d')}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-background-muted transition-colors"
          >
            <X className="h-4 w-4 text-foreground-muted" />
          </button>
        </div>

        {/* Content */}
        <div className="p-3 space-y-4">
          {/* Current status */}
          <div className="text-sm">
            <span className="text-foreground-muted">Standard hours: </span>
            <span className="font-medium text-foreground">
              {hasStandardHours
                ? `${standardRule.startTime.substring(0, 5)} - ${standardRule.endTime.substring(0, 5)}`
                : 'None (day off)'}
            </span>
          </div>

          {hasPto ? (
            /* PTO mode */
            <div className="space-y-3">
              <div className="p-3 bg-warning/20 rounded-md border border-warning/30">
                <p className="text-sm font-medium text-warning-foreground">
                  Marked as PTO
                </p>
                {currentPto.reason && (
                  <p className="text-xs text-warning-foreground/80 mt-1">
                    {currentPto.reason}
                  </p>
                )}
              </div>
              <button
                onClick={onClearPto}
                disabled={isSaving}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-danger hover:bg-danger-subtle rounded-md border border-danger/30 transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                Remove PTO
              </button>
            </div>
          ) : (
            /* Edit hours mode */
            <div className="space-y-4">
              {/* Time inputs */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-muted mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-muted mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              {/* Action buttons */}
              <div className="space-y-2">
                <button
                  onClick={handleSaveCustomHours}
                  disabled={isSaving}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary-hover rounded-md transition-colors disabled:opacity-50"
                >
                  <Clock className="h-4 w-4" />
                  {hasOverride ? 'Update Custom Hours' : 'Set Custom Hours'}
                </button>

                {hasOverride && (
                  <button
                    onClick={onClearOverride}
                    disabled={isSaving}
                    className="w-full px-3 py-2 text-sm font-medium text-foreground-muted hover:text-foreground border border-border rounded-md hover:bg-background-muted transition-colors disabled:opacity-50"
                  >
                    Use Standard Hours
                  </button>
                )}

                <button
                  onClick={onSetPto}
                  disabled={isSaving}
                  className="w-full px-3 py-2 text-sm font-medium text-warning-foreground border border-warning/50 rounded-md hover:bg-warning/10 transition-colors disabled:opacity-50"
                >
                  Mark as PTO
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
